/**
 * Usage Analytics Component
 * Displays usage trends, cost breakdowns, and token usage with date range selector
 * Requirements: 6.4
 */

import React, { useState, useEffect } from 'react';

interface TrendData {
  period: string;
  messages: number;
  tokens: number;
  cost: number;
}

interface CostBreakdown {
  byUser: Record<string, number>;
  byModel: Record<string, number>;
  byProvider: Record<string, number>;
}

interface UsageMetrics {
  totalMessages: number;
  totalTokens: number;
  totalCost: number;
  breakdown: Record<string, number>;
}

interface UsageReport {
  summary: UsageMetrics;
  trends: TrendData[];
  costBreakdown: CostBreakdown;
  period: {
    startDate: string;
    endDate: string;
  };
  generatedAt: string;
}

type TimeRange = 'day' | 'week' | 'month' | 'all';

const UsageAnalytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [report, setReport] = useState<UsageReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const endDate = new Date();
      const startDate = new Date();

      // Calculate start date based on time range
      switch (timeRange) {
        case 'day':
          startDate.setDate(endDate.getDate() - 1);
          break;
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'all':
          startDate.setFullYear(2020, 0, 1);
          break;
      }

      const aggregateBy = timeRange === 'all' ? 'month' : timeRange === 'month' ? 'week' : 'day';

      const response = await fetch(
        `/api/admin/analytics?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&aggregateBy=${aggregateBy}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setReport(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="text-red-600 dark:text-red-400">
          <p className="font-semibold">Error loading analytics</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const maxTrendValue = Math.max(...report.trends.map(t => t.cost));

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Usage Analytics
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setTimeRange('day')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 'day'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setTimeRange('week')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setTimeRange('month')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 'month'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setTimeRange('all')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                timeRange === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Time
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Total Messages
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatNumber(report.summary.totalMessages)}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Total Tokens
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatNumber(report.summary.totalTokens)}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Total Cost
          </h3>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatCurrency(report.summary.totalCost)}
          </p>
        </div>
      </div>

      {/* Usage Trends Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Usage Trends
        </h3>
        <div className="space-y-4">
          {report.trends.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No usage data available for this period
            </p>
          ) : (
            report.trends.map((trend, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {trend.period}
                  </span>
                  <div className="flex space-x-4 text-gray-600 dark:text-gray-400">
                    <span>{formatNumber(trend.messages)} msgs</span>
                    <span>{formatNumber(trend.tokens)} tokens</span>
                    <span className="font-semibold">{formatCurrency(trend.cost)}</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${maxTrendValue > 0 ? (trend.cost / maxTrendValue) * 100 : 0}%`,
                    }}
                  ></div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Cost Breakdown by Model */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Cost Breakdown by Model
        </h3>
        <div className="space-y-3">
          {Object.keys(report.costBreakdown.byModel).length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No model usage data available
            </p>
          ) : (
            Object.entries(report.costBreakdown.byModel)
              .sort(([, a], [, b]) => b - a)
              .map(([model, cost]) => (
                <div key={model} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {model}
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(cost)}
                  </span>
                </div>
              ))
          )}
        </div>
      </div>

      {/* Cost Breakdown by User */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Cost Breakdown by User
        </h3>
        <div className="space-y-3">
          {Object.keys(report.costBreakdown.byUser).length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No user usage data available
            </p>
          ) : (
            Object.entries(report.costBreakdown.byUser)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 10) // Show top 10 users
              .map(([userId, cost]) => (
                <div key={userId} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    User {userId.substring(0, 8)}...
                  </span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(cost)}
                  </span>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageAnalytics;
